import { Box, Flex, Skeleton } from '@chakra-ui/react'
import React, { useState } from 'react'
import styled from 'styled-components'
import { AudioPlayer } from '~/components/shared/AudioPlayer'
import { AudioPlayerVariant } from '~/components/shared/componentStyles/AudioPlayer.style'
import { SentimentSlider } from '~/pages/CallAnalytics/Transcription/Statistics/SentimentSlider'
import { CallTranscript } from '~/types/CallTranscript'

const AudioPlayerWrapper = styled.div`
  display: inline-block;
  min-height: 116px;
`

const StatsWrapper = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  justify-content: flex-start;
  margin: 0 -0.25rem 0 -0.25rem;
`

const MultiFillSliderWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin: -28px 2px 0px 2px;
  justify-content: space-between;
`

interface TranscriptAudioViewProps {
  audioUri: string
  callTranscript?: CallTranscript
  requestedTimestamp?: number
  requestedIsPlaying?: boolean
  onAudioPlayerPositionChange?: (position: number) => void
  onAudioPlayStateChange: (isPlaying: boolean) => void
}
const TranscriptAudioView = ({
  audioUri,
  callTranscript,
  requestedTimestamp,
  requestedIsPlaying = false,
  onAudioPlayerPositionChange,
  onAudioPlayStateChange: handleAudioPlayStateChange,
}: TranscriptAudioViewProps) => {
  const [isAudioPlayerReady, setIsAudioPlayerReady] = useState(false)
  const [didAudioPlayerHaveError, setDidAudioPlayerHaveError] = useState(false)

  const handleAudioPlayerPositionChange = (position: number) => {
    onAudioPlayerPositionChange?.(position)
  }

  const handleAudioPlayerReady = () => {
    setIsAudioPlayerReady(true)
  }

  const handleAudioPlayerLoadError = () => {
    setDidAudioPlayerHaveError(true)
  }

  const showPlayerSkeleton =
    !isAudioPlayerReady && !!audioUri && !didAudioPlayerHaveError

  return (
    <Flex flexDirection="column" flex="none" position="relative">
      {showPlayerSkeleton && (
        // We use an absolute positioned box so we can load the audio player in the background
        // while still showing the skeleton
        <Flex
          css={{
            backgroundColor: 'bg.panel',
            flexDirection: 'column',
            position: 'absolute',
            top: '-1rem',
            left: '-1.25rem',
            right: '-1.25rem',
            bottom: 0,
            zIndex: 2,
            padding: '1.25rem',
          }}
        >
          <Skeleton h="140px" />
        </Flex>
      )}
      {callTranscript ? (
        <>
          <AudioPlayerWrapper>
            <AudioPlayer
              variant={AudioPlayerVariant.Analytics}
              audioUri={audioUri}
              showJumpToAnswerButton={false}
              showInfoButton={false}
              showInlineInfoButton={true}
              requestedTimestamp={requestedTimestamp}
              requestedIsPlaying={requestedIsPlaying}
              onValueChanged={handleAudioPlayerPositionChange}
              onPlayStateChange={handleAudioPlayStateChange}
              onReady={handleAudioPlayerReady}
              onLoadError={handleAudioPlayerLoadError}
            />
          </AudioPlayerWrapper>

          <StatsWrapper>
            <MultiFillSliderWrapper>
              <SentimentSlider
                callTranscript={callTranscript}
                margin="4px 2px 0px 2px"
                participantRole="AGENT"
              />
              <SentimentSlider
                callTranscript={callTranscript}
                margin="4px 2px 0px 2px"
                participantRole="CUSTOMER"
              />
            </MultiFillSliderWrapper>
          </StatsWrapper>
        </>
      ) : (
        // This box is just here to make the audio player take up the final height as it loads
        <Flex css={{ backgroundColor: 'bg.panel', flexDirection: 'column' }}>
          <Box h="148px" />
        </Flex>
      )}
    </Flex>
  )
}

export default TranscriptAudioView
